import { useCallback, useRef, useState } from 'react';

/**
 * Swipe-down-to-dismiss for mobile bottom sheets. Drag is meant to be bound
 * to the handle row only so it never fights scrollable content below.
 */
export default function useSheetDrag(onClose) {
  const [dragY, setDragY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const dragStateRef = useRef(null);

  const onDragStart = useCallback((e) => {
    const t = e.touches[0];
    dragStateRef.current = { startY: t.clientY, startT: e.timeStamp, lastY: t.clientY, lastT: e.timeStamp };
    setIsDragging(true);
  }, []);

  const onDragMove = useCallback((e) => {
    const s = dragStateRef.current;
    if (!s) return;
    const t = e.touches[0];
    s.lastY = t.clientY;
    s.lastT = e.timeStamp;
    setDragY(Math.max(0, t.clientY - s.startY));
  }, []);

  const onDragEnd = useCallback(() => {
    const s = dragStateRef.current;
    dragStateRef.current = null;
    setIsDragging(false);
    if (!s) return;
    const dy = Math.max(0, s.lastY - s.startY);
    const velocity = dy / Math.max(1, s.lastT - s.startT);
    if (dy > 90 || (dy > 24 && velocity > 0.4)) {
      // Keep dragY so the sheet doesn't snap back up for a frame; the close
      // animation continues down from wherever the finger left it.
      onClose();
    } else {
      setDragY(0);
    }
  }, [onClose]);

  const resetDrag = useCallback(() => setDragY(0), []);

  const dragStyle = {
    transform: dragY > 0 ? `translateY(${dragY}px)` : undefined,
    transition: isDragging ? 'none' : undefined,
  };

  return { dragY, isDragging, onDragStart, onDragMove, onDragEnd, resetDrag, dragStyle };
}
